import { getLayoutViewportWidth, getLayoutViewportHeight } from './browserCapabilities';

export interface DeviceInfo {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  screenWidth: number;
  screenHeight: number;
  userAgent: string;
  deviceType: 'mobile' | 'tablet' | 'desktop';
}

export const getDeviceInfo = (): DeviceInfo => {
  const userAgent = navigator.userAgent;
  const screenWidth = window.screen.width;
  const screenHeight = window.screen.height;
  /** 布局宽度：优先 visualViewport / innerWidth，与 Chrome 移动端地址栏收起/展开一致 */
  const layoutWidth = getLayoutViewportWidth();
  const layoutHeight = getLayoutViewportHeight();
  
  // 检测移动设备（UA + 布局宽度，避免大屏手机被误判为桌面）
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent) ||
                   layoutWidth <= 768;
  
  // 检测平板设备
  const isTablet = /iPad|Android(?=.*\bMobile\b)(?=.*\bSafari\b)/i.test(userAgent) ||
                   (layoutWidth > 768 && layoutWidth <= 1024);
  
  // 检测桌面设备
  const isDesktop = !isMobile && !isTablet;
  
  let deviceType: 'mobile' | 'tablet' | 'desktop';
  if (isMobile) deviceType = 'mobile';
  else if (isTablet) deviceType = 'tablet';
  else deviceType = 'desktop';
  
  return {
    isMobile,
    isTablet,
    isDesktop,
    /** 供布局使用：与断点判断一致 */
    screenWidth: layoutWidth,
    screenHeight: layoutHeight,
    userAgent,
    deviceType,
  };
};

export const isTouchDevice = (): boolean => {
  return 'ontouchstart' in window || 
         navigator.maxTouchPoints > 0 || 
         (window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
};

export type Breakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

export interface BreakpointConfig {
  xs: number;
  sm: number;
  md: number;
  lg: number;
  xl: number;
  xxl: number;
}

export const breakpoints: BreakpointConfig = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1600,
};

export const getScreenSize = (): Breakpoint => {
  const width = getLayoutViewportWidth();
  if (width < breakpoints.sm) return 'xs';
  if (width < breakpoints.md) return 'sm';
  if (width < breakpoints.lg) return 'md';
  if (width < breakpoints.xl) return 'lg';
  if (width < breakpoints.xxl) return 'xl';
  return 'xxl';
};

export const isLandscape = (): boolean => {
  return getLayoutViewportWidth() > getLayoutViewportHeight();
};

export const isPortrait = (): boolean => {
  return getLayoutViewportHeight() > getLayoutViewportWidth();
};

export const getOrientation = (): 'landscape' | 'portrait' => {
  return isLandscape() ? 'landscape' : 'portrait';
};

export const isBreakpoint = (breakpoint: Breakpoint, direction: 'up' | 'down' = 'up'): boolean => {
  const width = getLayoutViewportWidth();
  const breakpointValue = breakpoints[breakpoint];
  
  if (direction === 'up') {
    return width >= breakpointValue;
  } else {
    return width < breakpointValue;
  }
};

export const isBetweenBreakpoints = (min: Breakpoint, max: Breakpoint): boolean => {
  const width = getLayoutViewportWidth();
  return width >= breakpoints[min] && width < breakpoints[max];
}; 